import React from "react";
import { Check, Loader2, X } from "lucide-react";
import {
  lockedProps,
  type GateProps,
} from "../../billing/hooks/useSubscriptionGate";

export interface PaymentAnswerProps {
  tone: "confirm" | "cancel";
  /** Names the icon, which has no text of its own, to a screen reader. */
  label: string;
  /** Off, with its reason, while the workspace has no plan behind it. */
  gate: GateProps;
  /**
   * Set while the press is still being acted on — an enquiry row has to fetch
   * the request it is answering before it can ask about it, and a tick that
   * looked untouched for that second would be pressed again.
   */
  busy?: boolean;
  onClick: () => void;
}

/**
 * One of the two answers an outstanding payment is waiting on, as the icon the
 * design puts in a Status cell.
 *
 * Neither commits anything on its own — both open a question — so they are
 * drawn alike, in the two colours the rest of the app already uses for "this
 * went through" and "this undoes something".
 *
 * Shared rather than drawn per screen: a client's payment history and an
 * enquiry row are asking the same thing of the same record, and a tick that
 * meant one thing in a table and something slightly different a screen away is
 * the drift this file exists to prevent.
 */
const PaymentAnswer: React.FC<PaymentAnswerProps> = ({
  tone,
  label,
  gate,
  busy = false,
  onClick,
}) => {
  const Icon = tone === "confirm" ? Check : X;

  return (
    <button
      type="button"
      aria-label={label}
      aria-busy={busy || undefined}
      // Marked disabled to a reader and turned back by hand rather than
      // natively disabled, so the press that did not know why is answered —
      // see `lockedProps`. The `title` it sets is the heading; on an icon this
      // small the pointer is the only place a reason could go before the press.
      {...lockedProps(gate, busy ? () => {} : onClick)}
      className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[6px] border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
        tone === "confirm"
          ? "border-brand bg-brand-soft text-brand-dark hover:bg-[#D2F5F7] focus-visible:ring-brand"
          : "border-[#F5B5B1] bg-[#FDECEC] text-[#FF3B30] hover:bg-[#FBDCDC] focus-visible:ring-[#FF3B30]"
      } ${gate.disabled || busy ? "cursor-not-allowed opacity-50" : ""}`}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <Icon className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
      )}
    </button>
  );
};

export default PaymentAnswer;
